import React, { useCallback, useMemo, useState } from "react";
import { ActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import { ActivityContextType } from "@/modules/lead-page/new/context/activity.context";
import { ActivityItem, ActivityType } from "@/modules/lead-page/dtos/index-v2";
import {
  useLeadTrackActivity,
  LEAD_TRACK_ACTIVITY_QUERY_KEY,
} from "../hooks/use-lead-track-activity.hook";
import { LeadTrackActivityItemDTO } from "../dtos";
import { useDeleteTrackComment } from "../hooks/use-delete-track-comment.hook";
import { getLeadTrackActivity } from "../api";
import { queryClient } from "@/queryClient";

export const DeveloperTrackActivityProvider: React.FC<{
  children: React.ReactNode;
  trackUId: string;
}> = ({ children, trackUId }) => {
  // Filters are not fully implemented for Developer Tracks yet, but we need to match the interface
  const [type, setType] = useState<ActivityType[]>([
    ActivityType.Comment,
    ActivityType.StatusUpdate,
    ActivityType.AssignmentUpdate,
  ]);

  const queryKey = useMemo(
    () => [LEAD_TRACK_ACTIVITY_QUERY_KEY, trackUId, 20, "desc", undefined],
    [trackUId]
  );

  const {
    data: rawData,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isError = false,
    isFetching,
    refetch,
  } = useLeadTrackActivity({
    trackUId,
    limit: 20,
    direction: "desc",
  });

  const forceRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  // Map the raw API response to the ActivityResponseDto expected by ActivityTab
  const data = useMemo(() => {
    if (!rawData) return undefined;

    return {
      pages: rawData.pages.map((page) => ({
        data: {
          items: page.items.map(mapLeadTrackActivityToActivityItem),
          count: page.count,
          nextCursor: page.nextCursor ?? "",
          prevCursor: page.prevCursor ?? "",
          hasNext: page.hasNext,
          hasPrev: page.hasPrev,
        },
      })),
      pageParams: rawData.pageParams,
    };
  }, [rawData]);

  // Bidirectional scrolling: fetchPreviousPage for loading newer items
  const hasPreviousPage = data?.pages[0]?.data?.hasPrev ?? false;
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false);

  const fetchPreviousPage = useCallback(async () => {
    if (!trackUId || !data || isFetchingPreviousPage || !hasPreviousPage)
      return;

    const prevCursor = data.pages[0]?.data?.prevCursor;
    if (!prevCursor) return;

    setIsFetchingPreviousPage(true);
    try {
      const response = await getLeadTrackActivity({
        trackUId,
        limit: 20,
        cursor: prevCursor,
        direction: "asc", // Load newer data
      });

      // Prepend the new page to the existing pages
      queryClient.setQueryData(queryKey, {
        pages: [response.data, ...rawData!.pages],
        pageParams: [
          prevCursor,
          ...rawData!.pageParams,
        ],
      });
    } finally {
      setIsFetchingPreviousPage(false);
    }
  }, [
    trackUId,
    data,
    rawData,
    isFetchingPreviousPage,
    hasPreviousPage,
    queryKey,
  ]);

  const filters = useMemo(
    () => ({
      type,
      setType,
    }),
    [type]
  );

  const [deletingId, setDeletingId] = useState<number | string | undefined>();
  const [dialogOpenConfirm, setDialogOpenConfirm] = useState(false);

  const { mutate: deleteComment, isLoading: loadDeleting } =
    useDeleteTrackComment({
      trackUId,
      onSuccess: () => {
        setDialogOpenConfirm(false);
        setDeletingId(undefined);
      },
    });

  const handleDeleteComments = useCallback(() => {
    if (deletingId) {
      deleteComment(deletingId.toString());
    }
  }, [deletingId, deleteComment]);

  const value: ActivityContextType = useMemo(
    () => ({
      filters,
      data,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      fetchPreviousPage,
      hasPreviousPage,
      isFetchingPreviousPage,
      isFetching,
      isLoading,
      isError,
      forceRefetch,
      handleDeleteComments,
      setDialogOpenConfirm,
      dialogOpenConfirm,
      loadDeleting,
      deletingId,
      setDeletingId,
      highlightedActivityId: undefined,
      setHighlightedActivityId: () => {},
    }),
    [
      filters,
      data,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      fetchPreviousPage,
      hasPreviousPage,
      isFetchingPreviousPage,
      isFetching,
      isLoading,
      isError,
      forceRefetch,
      handleDeleteComments,
      dialogOpenConfirm,
      loadDeleting,
      deletingId,
    ]
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

// Helper to extract status name whether it's an object or string
function getStatusName(status: any): string | null {
  if (!status) return null;
  if (typeof status === "string") return status;
  return status.name || null;
}

// Helper to extract status color, preferring the dedicated color field from the API
function getStatusColor(
  status: any,
  dedicatedColor: string | null | undefined
): string {
  // Prefer the dedicated color field (e.g. previousStatusColor / newStatusColor)
  if (dedicatedColor) return dedicatedColor;
  if (!status) return "#F5F5F5";
  if (typeof status === "string") return "#F5F5F5";
  return status.color || "#F5F5F5";
}

// Mapper Function
function mapLeadTrackActivityToActivityItem(
  item: LeadTrackActivityItemDTO
): ActivityItem {
  const baseItem: Partial<ActivityItem> = {
    id: item.id,
    uid: item.uid as any, // Casting to any to avoid UUID import from crypto
    createdAt: item.createdAt,
    actorName: item.actorName,
    actorColor: item.actorColor,
    content: item.content || item.comment || null,
    mentions: [],
    formData: item.formData || null,
    entityType: "Track",
    currentJourneyId: 0,
    deletedAt: item?.deletedAt || null,
  };

  const prevStatusName = getStatusName(item.previousStatus);
  const newStatusName = getStatusName(item.newStatus);

  const prevStatusColor = getStatusColor(
    item.previousStatus,
    item.previousStatusColor
  );
  const newStatusColor = getStatusColor(
    item.newStatus,
    item.newStatusColor
  );

  switch (item.type) {
    case "comment":
      return {
        ...baseItem,
        type: ActivityType.Comment,
      } as ActivityItem;

    case "status_update":
      return {
        ...baseItem,
        type: ActivityType.StatusUpdate,
        previousStatus: prevStatusName,
        previousStatusColor: prevStatusColor,
        newStatus: newStatusName,
        newStatusColor: newStatusColor, // Pass background color for the badge
      } as ActivityItem;

    case "assignment_update":
      return {
        ...baseItem,
        type: ActivityType.AssignmentUpdate,
        previousUser: item.previousDeveloperUser?.name || null,
        // previousUserColor: item.previousDeveloperUser?.color || null, // DeveloperUserDTO has no color
        previousUserColor: null,
        newUser: item.newDeveloperUser?.name || null,
        // newUserColor: item.newDeveloperUser?.color || null,
        newUserColor: null,
      } as ActivityItem;

    default:
      // Fallback for unknown types
      return {
        ...baseItem,
        type: ActivityType.Comment,
      } as ActivityItem;
  }
}
